import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Container, Grid, Typography, Box, Button, Chip, 
  Divider, Paper, Skeleton, Stack 
} from '@mui/material';
import useStore from '../store/useStore';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import VerifiedIcon from '@mui/icons-material/Verified';
import KeyboardBackColor from '@mui/icons-material/KeyboardBackspace';
import { useJsonLd } from '../hooks/useJsonLd';

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetchProductDetails } = useStore();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await fetchProductDetails(id);
        setProduct(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id, fetchProductDetails]);

  // Preparamos el objeto para el agente
  // Dentro de tu componente, una vez que 'product' ya tiene los datos del back:
  const structuredData = product ? {
    "@context": "https://schema.org/",
    "@type": "Product",
    "name": product.product_details.name,
    "description": product.product_details.description || "Sin descripción disponible.",
    "image": product.image_url, 
    "sku": product.product_details.id,
    "brand": {
      "@type": "Brand",
      "name": product.product_details.brand || "Stiletto"
    },
    "offers": {
      "@type": "Offer",
      "priceCurrency": product.economics.currency || "BOB",
      "price": product.economics.price,
      "availability": "https://schema.org/InStock",
      "inventoryLevel": {
        "@type": "QuantitativeValue",
        "value": product.economics.stock_count
      },
      "url": window.location.href, // Importante para que la oferta se vincule a la URL actual
      "itemCondition": "https://schema.org/NewCondition",
      "priceSpecification": {
        "@type": "PriceSpecification",
        "price": product.economics.price,
        "priceCurrency": product.economics.currency || "BOB",
        "valueAddedTaxIncluded": true
      },
      "seller": {
        "@type": "Organization",
        "name": "Stiletto Bolivia"
      },
      "acceptedPaymentMethod": [
        { "@type": "PaymentMethod", "name": "BankTransfer" },
        { "@type": "PaymentMethod", "name": "QRPayment" }
      ]
    },
    "shippingDetails": {
      "@type": "OfferShippingDetails",
      "shippingDestination": {
        "@type": "DefinedRegion",
        "addressCountry": "BO"
      },
      "deliveryTime": {
        "@type": "ShippingDeliveryTime",
        "handlingTime": {
          "@type": "QuantitativeValue",
          "minValue": 0,
          "maxValue": 1,
          "unitCode": "DAY"
        }
      }
    },
    "additionalProperty": [
      {
        "@type": "PropertyValue",
        "name": "best_for",
        "value": (product.decision_metrics?.best_for || []).join(", ")
      },
      {
        "@type": "PropertyValue",
        "name": "avoid_if",
        "value": (product.decision_metrics?.avoid_if || []).join(", ")
      },
      {
        "@type": "PropertyValue",
        "name": "confidence_score",
        "value": product.decision_metrics?.confidence_score || "high"
      },
      {
        "@type": "PropertyValue",
        "name": "ai_reasoning",
        "value": product.decision_metrics?.reasoning || ""
      },
      {
        "@type": "PropertyValue",
        "name": "value_rating",
        "value": product.decision_metrics?.value_rating || 0
      },
      {
        "@type": "PropertyValue",
        "name": "guarantee",
        "value": product.trust_signals?.guarantee_policy || "7 days"
      }
    ],
    "dateModified": product.metadata?.last_updated || new Date().toISOString()
  } : null;

  // Usamos el hook manual que creamos para inyectarlo al final del body
  useJsonLd(structuredData);

  // Actualizar el título manualmente (más fiable en React 19)
  useEffect(() => {
    if (product) {
      document.title = `${product.product_details.name} | Stiletto Shop`;
    }
  }, [product]);

  if (loading) return <LoadingSkeleton />;
  if (!product) return <Typography>Producto no encontrado</Typography>;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>

      <Button startIcon={<KeyboardBackColor />} onClick={() => navigate(-1)} sx={{ mb: 2, color: 'text.secondary' }}>
        Volver al catálogo
      </Button>

      <Grid container spacing={6}>
        {/* LADO IZQUIERDO: IMAGEN */}
        {product.imagenUrl && <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ borderRadius: 4, overflow: 'hidden', border: '1px solid #f0f0f0' }}>
            <img 
              src={product.imagenUrl} 
              alt={product.product_details.name}
              style={{ width: '100%', height: 'auto', display: 'block' }}
            />
          </Paper>
        </Grid>}

        {/* LADO DERECHO: INFO */}
        <Grid item xs={12} md={6}>
          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 'bold' }}>
            {product.product_details.category}
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 800, mb: 1, lineHeight: 1.1 }}>
            {product.product_details.name}
          </Typography>
          
          <Typography variant="h4" sx={{ fontWeight: 900, color: '#333', mb: 3 }}>
            BOB {product.economics.price.toFixed(2)}
          </Typography>

          <Divider sx={{ mb: 3 }} />

          {/* SECCIÓN GXO: AGENT DECISION METRICS */}
          <Box sx={{ mb: 4, p: 2, bgcolor: '#f9fafb', borderRadius: 2, border: '1px dashed #cbd5e1' }}>
            <Typography variant="subtitle2" sx={{ color: '#64748b', mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <VerifiedIcon sx={{ fontSize: 16 }} /> Análisis del Agente Stiletto
            </Typography>
            
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
              "{product.decision_metrics.reasoning}"
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" display="block" sx={{ color: '#64748b', mb: 0.5 }}>Ideal para:</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {product.decision_metrics.best_for.map(tag => (
                  <Chip key={tag} label={tag} size="small" sx={{ bgcolor: 'white', border: '1px solid #e2e8f0' }} />
                ))}
              </Stack>
            </Box>

            {product.decision_metrics.avoid_if.length > 0 && (
              <Box>
                <Typography variant="caption" display="block" sx={{ color: '#e11d48', mb: 0.5 }}>Evitar si:</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {product.decision_metrics.avoid_if.map(tag => (
                    <Chip key={tag} label={tag} size="small" variant="outlined" color="error" />
                  ))}
                </Stack>
              </Box>
            )}
          </Box>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.7 }}>
            {product.product_details.description || "Sin descripción adicional."}
          </Typography>

          <Stack spacing={2}>
            <Button 
              fullWidth 
              variant="contained" 
              size="large" 
              startIcon={<ShoppingCartIcon />}
              sx={{ bgcolor: '#000', py: 2, borderRadius: 3, fontWeight: 'bold', '&:hover': { bgcolor: '#333' } }}
            >
              Añadir al carrito
            </Button>
            
            <Typography variant="caption" textAlign="center" color="text.secondary">
              Garantía de Stiletto: {product.trust_signals.guarantee_policy}
            </Typography>
          </Stack>
        </Grid>
      </Grid>
    </Container>
  );
}

function LoadingSkeleton() {
  return (
    <Container maxWidth="lg" sx={{ mt: 10 }}>
      <Grid container spacing={6}>
        <Grid item xs={12} md={6}><Skeleton variant="rectangular" height={600} sx={{ borderRadius: 4 }} /></Grid>
        <Grid item xs={12} md={6}>
          <Skeleton width="40%" height={30} />
          <Skeleton width="80%" height={60} />
          <Skeleton width="30%" height={50} sx={{ mt: 2 }} />
          <Skeleton variant="rectangular" height={200} sx={{ mt: 4, borderRadius: 2 }} />
        </Grid>
      </Grid>
    </Container>
  );
}